// Filterzeile des Verlaufs (T48): Suche mit „alle/irgendein Begriff“, Mitarbeiter, Lead, Zeitraum, weitere Filter (Richtung, Dauer,
// Ergebnis, nur Aufgenommene, auch Anrufe ohne Gespräch) und Sortierung. Der Zustand steht in der Adresse; hier wird er nur gezeigt.
import { useEffect, useState } from "react";
import { sucheApi } from "../api";
import type { NutzerKurz, SuchTreffer } from "../api/types";
import { ERGEBNISSE } from "../aktivitaeten/texte";
import { ZEITRAEUME, zeitraumText, type Zeitraum } from "../aktivitaeten/zeiten";
import { Ausklapp } from "../components/Ausklapp";
import { Auswahl } from "../components/Auswahl";
import { Icon } from "../components/Icon";
import { KompaktWert } from "../kontakte/liste/KompaktWert";
import { filterGesetzt, suchbegriffe, weitereFilterZahl, type VerlaufFilter } from "./logik";

interface Props {
  filter: VerlaufFilter;
  suche: string;
  onSuche: (text: string) => void;
  onAendern: (teil: Partial<VerlaufFilter>) => void;
  onLeeren: () => void;
  nutzer: NutzerKurz[];
  /** darf Gespräche anderer sehen (sonst entfällt die Mitarbeiter-Auswahl) */
  alle: boolean;
  total: number | null;
  /** Link zur aktuellen Ansicht kopieren */
  onLink: () => void;
}

function LeadFilter({ lead, leadName, onWahl }: { lead: string | null; leadName: string | null; onWahl: (id: string | null, name: string | null) => void }) {
  const [knopf, setKnopf] = useState<HTMLButtonElement | null>(null);
  const [offen, setOffen] = useState(false);
  const [text, setText] = useState("");
  const [treffer, setTreffer] = useState<SuchTreffer[]>([]);
  const [laedt, setLaedt] = useState(false);

  useEffect(() => {
    if (!offen) return;
    const q = text.trim();
    if (q.length < 2) {
      setTreffer([]);
      return;
    }
    const ctrl = new AbortController();
    setLaedt(true);
    const t = window.setTimeout(() => {
      sucheApi
        .suchen(q, 12, ctrl.signal)
        .then((r) => {
          const gesehen = new Set<string>();
          setTreffer(r.data.filter((x) => (gesehen.has(x.lead_id) ? false : (gesehen.add(x.lead_id), true))));
        })
        .catch(() => undefined)
        .finally(() => setLaedt(false));
    }, 200);
    return () => {
      window.clearTimeout(t);
      ctrl.abort();
    };
  }, [text, offen]);

  return (
    <>
      <button
        ref={setKnopf}
        type="button"
        className={`gesp-pille${lead ? " gesp-pille--an" : ""}`}
        aria-haspopup="dialog"
        aria-expanded={offen}
        title="Nach Lead filtern"
        onClick={() => setOffen((o) => !o)}
      >
        <Icon name="leads" groesse={13} />
        <span className="gesp-pille__text">{lead ? leadName ?? "Lead" : "Alle Leads"}</span>
        {lead ? (
          <span
            role="button"
            tabIndex={0}
            aria-label="Lead-Filter entfernen"
            onClick={(e) => (e.stopPropagation(), onWahl(null, null))}
            onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), e.stopPropagation(), onWahl(null, null))}
          >
            <Icon name="schliessen" groesse={12} />
          </span>
        ) : (
          <Icon name="chevron-unten" groesse={12} />
        )}
      </button>
      <Ausklapp anker={knopf} offen={offen} onSchliessen={() => setOffen(false)} breite={320} rolle="dialog">
        <div className="gesp-leadwahl">
          <input autoFocus value={text} placeholder="Lead suchen …" aria-label="Lead suchen" onChange={(e) => setText(e.target.value)} />
          {treffer.length ? (
            <ul className="gesp-leadwahl__liste">
              {treffer.map((t) => (
                <li key={t.lead_id}>
                  <button
                    type="button"
                    className="gesp-leadwahl__eintrag"
                    onClick={() => {
                      onWahl(t.lead_id, t.type === "lead" ? t.title : t.subtitle ?? t.title);
                      setOffen(false);
                    }}
                  >
                    {t.type === "lead" ? t.title : t.subtitle ?? t.title}
                    {t.type === "contact" ? <span className="gesp-leadwahl__unter">Kontakt: {t.title}</span> : t.subtitle ? <span className="gesp-leadwahl__unter">{t.subtitle}</span> : null}
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <div className="gesp-leadwahl__leer">{text.trim().length < 2 ? "Mindestens zwei Buchstaben eingeben." : laedt ? "Sucht …" : "Kein Lead gefunden."}</div>
          )}
        </div>
      </Ausklapp>
    </>
  );
}

function WeitereFilter({ filter, onAendern }: { filter: VerlaufFilter; onAendern: (teil: Partial<VerlaufFilter>) => void }) {
  const [knopf, setKnopf] = useState<HTMLButtonElement | null>(null);
  const [offen, setOffen] = useState(false);
  const zahl = weitereFilterZahl({ ...filter, zeitraum: "alle" });
  const richtungen: { wert: VerlaufFilter["richtung"]; label: string }[] = [
    { wert: null, label: "Alle" },
    { wert: "ausgehend", label: "Ausgehend" },
    { wert: "eingehend", label: "Eingehend" },
  ];
  return (
    <>
      <button
        ref={setKnopf}
        type="button"
        className={`gesp-pille${zahl ? " gesp-pille--an" : ""}`}
        aria-haspopup="dialog"
        aria-expanded={offen}
        onClick={() => setOffen((o) => !o)}
      >
        <Icon name="filter" groesse={13} />
        <span className="gesp-pille__text">Weitere Filter</span>
        {zahl ? <span className="gesp-pille__zahl">{zahl}</span> : null}
      </button>
      <Ausklapp anker={knopf} offen={offen} onSchliessen={() => setOffen(false)} breite={340} rolle="dialog">
        <div className="gesp-popover">
          <div className="gesp-popover__zeile">
            <span className="gesp-popover__label">Richtung</span>
            <span className="segment" role="group" aria-label="Richtung">
              {richtungen.map((r) => (
                <button
                  key={r.label}
                  type="button"
                  className={`segment__knopf${filter.richtung === r.wert ? " segment__knopf--an" : ""}`}
                  aria-pressed={filter.richtung === r.wert}
                  onClick={() => onAendern({ richtung: r.wert })}
                >
                  {r.label}
                </button>
              ))}
            </span>
          </div>
          <div className="gesp-popover__zeile">
            <label className="gesp-popover__label" htmlFor="gesp-min">
              Dauer mindestens (Minuten)
            </label>
            <input
              id="gesp-min"
              className="gesp-popover__zahl"
              type="number"
              min={0}
              max={1440}
              inputMode="numeric"
              value={filter.minMinuten ?? ""}
              placeholder="0"
              onChange={(e) => onAendern({ minMinuten: e.target.value ? Math.max(0, Math.min(1440, Math.floor(Number(e.target.value)))) || null : null })}
            />
          </div>
          <div className="gesp-popover__zeile">
            <span className="gesp-popover__label">Ergebnis</span>
            <span style={{ width: 190 }}>
              <Auswahl
                optionen={ERGEBNISSE.map((e) => ({ wert: e.wert, label: e.label }))}
                wert={filter.ergebnisse}
                mehrfach
                leerErlaubt
                platzhalter="Alle Ergebnisse"
                breite={240}
                titel="Nach Anrufergebnis filtern"
                knopfInhalt={filter.ergebnisse.length > 1 ? <KompaktWert text={`${filter.ergebnisse.length} Ergebnisse`} /> : undefined}
                onWahl={(w) => onAendern({ ergebnisse: w as string[] })}
              />
            </span>
          </div>
          <label className="gesp-schalter">
            <input type="checkbox" checked={filter.nurAufgenommen} onChange={(e) => onAendern({ nurAufgenommen: e.target.checked })} />
            <span>
              Nur Gespräche mit Aufnahme
              <span className="gesp-schalter__unter">Aufnahmen aus Close und Mailbox-Nachrichten, soweit sie noch abspielbar sind.</span>
            </span>
          </label>
          <label className="gesp-schalter">
            <input type="checkbox" checked={filter.alle} onChange={(e) => onAendern({ alle: e.target.checked })} />
            <span>
              Auch Anrufe ohne Gespräch
              <span className="gesp-schalter__unter">Nicht erreicht, besetzt, ohne Notiz.</span>
            </span>
          </label>
        </div>
      </Ausklapp>
    </>
  );
}

export function VerlaufFilterZeile({ filter, suche, onSuche, onAendern, onLeeren, nutzer, alle, total, onLink }: Props) {
  const nutzerOptionen = nutzer.map((n) => ({ wert: n.close_user_id ?? n.id, label: n.name, unter: n.is_active ? null : "inaktiv" }));
  const zeitOptionen = ZEITRAEUME.filter((z) => z.id !== "alle").map((z) => ({ wert: z.id, label: z.label }));
  const mehrereBegriffe = suchbegriffe(suche).length > 1;
  return (
    <div className="gesp-filter" role="search">
      <label className="suchfeld gesp-filter__suche">
        <Icon name="suche" groesse={14} />
        <input
          type="search"
          value={suche}
          placeholder="Suche in Notizen, Leads, Kontakten und Nummern …"
          aria-label="Gespräche durchsuchen"
          onChange={(e) => onSuche(e.target.value)}
        />
      </label>
      {mehrereBegriffe ? (
        <span className="gesp-filter__feld">
          <Auswahl
            optionen={[
              { wert: "alle", label: "Alle Begriffe" },
              { wert: "irgendein", label: "Irgendein Begriff" },
            ]}
            wert={filter.match}
            breite={190}
            titel="Wie müssen die Suchbegriffe passen?"
            onWahl={(w) => onAendern({ match: w === "irgendein" ? "irgendein" : "alle" })}
          />
        </span>
      ) : null}
      {alle ? (
        <span className="gesp-filter__feld">
          <Auswahl
            optionen={nutzerOptionen}
            wert={filter.user}
            leerErlaubt
            platzhalter="Alle Mitarbeiter"
            breite={240}
            titel="Nach Mitarbeiter filtern"
            onWahl={(w) => onAendern({ user: (w as string | null) || null })}
          />
        </span>
      ) : null}
      <LeadFilter lead={filter.lead} leadName={filter.leadName} onWahl={(id, name) => onAendern({ lead: id, leadName: name })} />
      <span className="gesp-filter__feld gesp-filter__feld--zeit">
        <Auswahl
          optionen={zeitOptionen}
          wert={filter.zeitraum === "alle" ? null : filter.zeitraum}
          leerErlaubt
          platzhalter="Alle Zeiten"
          breite={200}
          titel="Nach Zeitraum filtern"
          knopfInhalt={filter.zeitraum === "eigen" ? <KompaktWert text={zeitraumText("eigen", filter.eigenVon, filter.eigenBis)} /> : undefined}
          onWahl={(w) => {
            const z: Zeitraum = typeof w === "string" && ZEITRAEUME.some((x) => x.id === w) ? (w as Zeitraum) : "alle";
            onAendern({ zeitraum: z, ...(z !== "eigen" ? { eigenVon: null, eigenBis: null } : {}) });
          }}
        />
      </span>
      {filter.zeitraum === "eigen" ? (
        <span className="gesp-daten">
          <input
            type="date"
            className="gesp-datum"
            aria-label="Von (Datum)"
            value={filter.eigenVon ?? ""}
            max={filter.eigenBis ?? undefined}
            onChange={(e) => onAendern({ eigenVon: e.target.value || null })}
          />
          <span className="leise">bis</span>
          <input
            type="date"
            className="gesp-datum"
            aria-label="Bis (Datum, einschließlich)"
            value={filter.eigenBis ?? ""}
            min={filter.eigenVon ?? undefined}
            onChange={(e) => onAendern({ eigenBis: e.target.value || null })}
          />
        </span>
      ) : null}
      <WeitereFilter filter={filter} onAendern={onAendern} />
      <span className="gesp-filter__feld gesp-filter__feld--sort">
        <Auswahl
          optionen={[
            { wert: "neu", label: "Neueste zuerst" },
            { wert: "alt", label: "Älteste zuerst" },
            { wert: "lang", label: "Längste zuerst" },
          ]}
          wert={filter.sort}
          breite={190}
          titel="Sortierung"
          onWahl={(w) => onAendern({ sort: w === "alt" || w === "lang" ? w : "neu" })}
        />
      </span>
      {filterGesetzt(filter) ? (
        <button type="button" className="knopf knopf--leise knopf--klein" onClick={onLeeren}>
          Filter leeren
        </button>
      ) : null}
      <span className="gesp-filter__zaehlung">
        {total === null ? "" : `${total.toLocaleString("de-DE")} ${total === 1 ? "Gespräch" : "Gespräche"}`}
        <button type="button" className="symbolknopf symbolknopf--klein" title="Link zu dieser Ansicht kopieren" aria-label="Link zu dieser Ansicht kopieren" onClick={onLink}>
          <Icon name="link" groesse={14} />
        </button>
      </span>
    </div>
  );
}
